import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { PendingButton } from '@/components/ui';
import { useGenerateReport } from '@/hooks/queries/useFinanceReports';
import { useCurrentPeriodReport } from '@/hooks/queries/useCurrentPeriodReport';
import { ReportDetailPage } from '../reports/ReportDetailPage';
import { CloseCaughtUpEmptyState } from './CloseCaughtUpEmptyState';
import { formatDate } from '../utils';

/**
 * CPH-014 sub-route at `/admin/finance/close/file`. Mounts the EXISTING
 * report-preview / preflight / filing flow (FPD-002) under the
 * `CloseLayout` chrome so deep-linking + browser back work cleanly and the
 * "Back to report" affordance from CPH-011 persists.
 *
 * Resolution path:
 *   - No open period (404 NoOpenPeriod) → honest empty state.
 *   - Period has a report → mount {@link ReportDetailPage} for it.
 *   - Period has no report yet → "Generate report" CTA → POST `/reports/generate`
 *     with `{ deadlineId }`, then navigate to the new report's detail.
 *
 * This ticket does NOT modify FPD-002 (audit logging, attestation,
 * preflight semantics) — it only routes the treasurer into it.
 */
export function CloseFileRoute() {
  const navigate = useNavigate();
  // AFU-006 — the period→report resolution now lives in the shared
  // useCurrentPeriodReport hook so the close hub's filing-readiness surface
  // resolves the identical report this File flow does.
  const current = useCurrentPeriodReport();
  const { generateReport } = useGenerateReport();
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  // With health unanswered the owed report is unknown, and the calendar-open period may not be it.
  // Offer no filing rather than file the wrong period; the masthead says the same thing.
  if (current.status === 'failed' || current.status === 'offline') {
    return (
      <div className="p-6">
        <Breadcrumb />
        <p
          data-testid="close-file-health-unavailable"
          className="mt-6 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
        >
          {current.status === 'offline'
            ? 'You’re offline. Which report you owe, and filing it, will load when you reconnect.'
            : 'We couldn’t load which report you owe, so we can’t tell which period to file. Refresh to retry.'}
        </p>
      </div>
    );
  }

  const isLoading = current.status !== 'ready';
  const period = current.status === 'ready' ? current.data.period : null;
  const reportForPeriod = current.status === 'ready' ? current.data.report : null;
  const deadlineId = period?.id;

  if (!period && !isLoading) {
    // No open period (filed early, or between periods). Reassure the treasurer
    // they're caught up and point them at their filed reports / the reopen path,
    // instead of a cold "nothing open" sentence.
    return (
      <div className="p-6">
        <Breadcrumb />
        <div className="mt-6">
          <CloseCaughtUpEmptyState />
        </div>
      </div>
    );
  }

  if (reportForPeriod) {
    return (
      <div className="p-6">
        <Breadcrumb />
        <div className="mt-4">
          <ReportDetailPage reportId={reportForPeriod.id} />
        </div>
      </div>
    );
  }

  // Period exists but no report yet — the Generate CTA — OR the period→report
  // resolution is still in flight. Both render the same CTA: while resolving,
  // it's a disabled, spinning PendingButton, so "we're computing whether you're
  // ready to file" reads the same as every other in-flight action instead of a
  // bare "Loading…" text line. Clicking Generate then navigates to the report.
  return (
    <div className="p-6">
      <Breadcrumb />
      <div className="mt-6 rounded-md border border-border bg-card p-6 text-card-foreground">
        {period ? (
          <>
            <h2 className="text-base font-semibold">Ready to file this period</h2>
            <p className="mt-1 text-sm font-medium text-foreground" data-testid="close-file-period">
              {formatDate(period.periodStart)} – {formatDate(period.periodEnd)}
              <span className="font-normal text-muted-foreground"> · due {formatDate(period.dueDate)}</span>
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Generate the report to start the review-and-file flow.
            </p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="close-file-checking">
            Checking whether this period is ready to file…
          </p>
        )}
        <PendingButton
          type="button"
          data-testid="close-file-generate-report"
          pending={generating || isLoading}
          onClick={async () => {
            setGenerateError(null);
            setGenerating(true);
            try {
              const result = await generateReport({ deadlineId: deadlineId! });
              void navigate({
                to: '/admin/finance/reports/$reportId',
                params: { reportId: result.report.id },
              });
            } catch (err) {
              setGenerateError(
                err instanceof Error ? err.message : 'Failed to generate report',
              );
            } finally {
              setGenerating(false);
            }
          }}
          className="mt-4"
        >
          Generate report
        </PendingButton>
        {generateError && (
          <p
            role="alert"
            data-testid="close-file-generate-error"
            className="mt-2 text-sm text-destructive"
          >
            {generateError}
          </p>
        )}
      </div>
    </div>
  );
}

function Breadcrumb() {
  return (
    <nav
      data-testid="close-file-breadcrumb"
      aria-label="Breadcrumb"
      className="text-xs text-muted-foreground"
    >
      <Link
        to="/admin/finance/close"
        className="hover:text-foreground hover:underline"
      >
        Close the period
      </Link>
      <span aria-hidden="true" className="mx-1.5">
        ›
      </span>
      <span
        data-testid="close-file-breadcrumb-current"
        className="text-foreground"
      >
        File
      </span>
    </nav>
  );
}
